/**
 * La table des leads du recrutement — qui a candidaté, et ce qu'on sait de lui.
 *
 * Kayla, 18-08-2026 : « une table où je peux voir leur nom, prénom, sexe, leur
 * disponibilité, leur Discord, leur adresse mail […] et je peux les trier ».
 *
 * Le tri et les filtres NE SONT PAS ici : ils vivent dans `../leads.ts`, qui
 * est du calcul pur et se teste comme tel. Ce fichier ne fait que les rendre
 * cliquables.
 *
 * 🚫 Aucun filtre par défaut. Le compte affiché est « N leads » quand rien
 * n'est coché, et « N sur M » dès qu'un filtre mord — un écran qui cache en
 * ayant l'air complet coûte plus cher qu'un écran qui montre trop. Les 18
 * liens jamais ouverts qui traînent en base ont le droit d'être vus : c'est
 * une information sur la campagne, pas du bruit à masquer.
 *
 * 🩸 LES CINQ RANGÉES DE PASTILLES SONT PARTIES DERRIÈRE UN BOUTON le
 * 19-08-2026. Kayla : « j'aime pas comment c'est fait, les filtres. Pourquoi
 * tu n'as pas fait comme on a dans Conversation, juste un bouton Filtre ? »
 * Elles occupaient un tiers de l'écran en permanence pour un réglage qu'on
 * touche une fois par session, et repoussaient la table sous la ligne de
 * flottaison.
 *
 * ⚠️ Ce qui filtre doit alors se lire SANS ouvrir le panneau, sinon on
 * retombe sur le filtre-par-défaut-invisible que l'alinéa du dessus interdit.
 * D'où les deux choses que ce fichier tient : le compte sur le bouton Filtre,
 * et une puce par catégorie active — chacune levable seule (`filtresActifs`
 * dans `../leads.ts`, calcul pur et testé).
 *
 * 01-11-2026 — au nouveau thème : la table est le `Tableau` de la librairie
 * (verre arrondi, tri sur l'en-tête, pagination calée sur la hauteur : le corps
 * ne défile jamais, et plus de défilement horizontal non plus — sous
 * `SEUIL_ETROIT` seules les colonnes essentielles restent, et chaque colonne
 * attend sa largeur : `desDe`). La recherche, les puces et le bouton Filtre
 * sont ceux de la librairie. Le tri, lui, reste CONTRÔLÉ : le cycle d'ici
 * (`trierPar`) ne change pas.
 */
import { useMemo, useState, type ReactNode } from "react";

import { Alerte, Badge, Button, Infobulle, Saisie, Tableau, Temps, type Colonne } from "@/shared/ui";
import { DiscordLogo, Download } from "@/shared/ui/icons";
import { FUSEAU_DES_SHIFTS } from "@/shared/shiftTime";

import { CANDIDATES_CSV_HREF } from "../api";
import { CONN_VERDICT_LABELS, SHIFTS } from "../constants";
import { statusLabel } from "../format";
import {
  estEcarte,
  filtresActifs,
  FILTRES_VIDES,
  leadsAffiches,
  nombreDeCreneaux,
  nombreDEcartes,
  nombreDeLiensVides,
  shiftsCouverts,
  type ColonneTri,
  type FiltresLeads,
  type Tri,
} from "../leads";

import { lienDuDm, type DiscordTrouve } from "../discordDesLeads";
import type { CandidateListItem, ConnVerdict } from "../types";

import { LeadsFiltresPopover } from "./LeadsFiltresPopover";

interface LeadsTableProps {
  leads: CandidateListItem[];
  loading: boolean;
  onSelect: (id: string) => void;
  /**
   * Le compte Discord qu'on sait ouvrir, par `id` de lead — calculé par
   * l'appelant (`discordParLead`, pur et testé).
   *
   * Absent = la table se comporte comme avant : le pseudo, en texte. C'est le
   * cas de la page `/regie-test-admin`, qui n'a pas d'organisation sous la
   * main. Un lead sans entrée dans la map n'a pas de bouton — jamais un
   * bouton qui ouvrirait le mauvais profil.
   */
  discordParLead?: Map<string, DiscordTrouve>;
}

const SEXE_LABEL: Record<string, string> = { homme: "Homme", femme: "Femme" };

/** Sous cette largeur de tableau (px), seules Prénom · Nom · Test restent. */
const SEUIL_ETROIT = 620;
/** La hauteur du cadre : il se pagine dedans, la page défile autour. */
const HAUTEUR_DU_TABLEAU = 700;
const HAUTEUR_LIGNE = 52;

/** Le ton du verdict de connexion : bonne = validé, correcte = attention, instable = erreur. */
const TON_CONNEXION: Record<ConnVerdict, "vert" | "ambre" | "rouge"> = {
  good: "vert",
  ok: "ambre",
  bad: "rouge",
};

/** Un écarté qu'on a rappelé se distingue d'un vivant : grisé. */
function griser(c: CandidateListItem, contenu: ReactNode) {
  return estEcarte(c) ? <span className="opacity-50">{contenu}</span> : contenu;
}

export function LeadsTable({
  leads,
  loading,
  onSelect,
  discordParLead,
}: LeadsTableProps) {
  const [filtres, setFiltres] = useState<FiltresLeads>(FILTRES_VIDES);
  const [tri, setTri] = useState<Tri>({ colonne: "recu_le", sens: "desc" });

  const affiches = useMemo(() => leadsAffiches(leads, filtres, tri), [leads, filtres, tri]);
  // Ce qui filtre en ce moment, panneau fermé — une puce par catégorie.
  const actifs = useMemo(() => filtresActifs(filtres), [filtres]);
  const videsCaches = useMemo(() => nombreDeLiensVides(leads, filtres), [leads, filtres]);
  const ecartesCaches = useMemo(() => nombreDEcartes(leads, filtres), [leads, filtres]);
  /** Le dénominateur honnête : « N sur M » doit compter ce qu'on POUVAIT
   *  montrer, pas les liens vides ni les écartés qu'on a rangés d'office. */
  const total = leads.length - videsCaches - ecartesCaches;

  /** « N sur M » ne s'affiche que si quelque chose mord VRAIMENT. Les puces
   *  disent déjà quoi ; la recherche s'y ajoute, elle a son champ. */
  const filtreActif = actifs.length > 0 || filtres.recherche.trim() !== "";

  function trierPar(colonne: ColonneTri) {
    setTri((t) =>
      t.colonne === colonne
        ? { colonne, sens: t.sens === "asc" ? "desc" : "asc" }
        : // Premier clic : l'ordre le plus utile pour la colonne. On veut voir
          // la MEILLEURE connexion et les PLUS RÉCENTS d'abord, mais les noms
          // de A à Z.
          { colonne, sens: colonne === "recu_le" ? "desc" : "asc" },
    );
  }

  const colonnes = useMemo<Colonne<CandidateListItem>[]>(
    () => [
      {
        cle: "prenom",
        titre: "Prénom",
        triable: true,
        essentiel: true,
        largeur: 96,
        largeurEtroite: "auto",
        rendu: (c) => griser(c, <span className="font-bold text-fg">{c.first_name ?? "—"}</span>),
      },
      {
        cle: "nom",
        titre: "Nom",
        triable: true,
        essentiel: true,
        largeur: 96,
        largeurEtroite: "auto",
        rendu: (c) => griser(c, <span className="text-fg">{c.last_name ?? "—"}</span>),
      },
      {
        cle: "sexe",
        titre: "Sexe",
        triable: true,
        desDe: 1200,
        largeur: 72,
        rendu: (c) =>
          griser(c, <span className="text-muted">{c.gender ? (SEXE_LABEL[c.gender] ?? c.gender) : "—"}</span>),
      },
      {
        cle: "age",
        titre: "Âge",
        triable: true,
        aligne: "droite",
        desDe: 940,
        largeur: 76,
        rendu: (c) =>
          griser(
            c,
            c.age === null || c.age === undefined ? (
              <span className="text-faint">—</span>
            ) : (
              <span className="tabular-nums text-fg">{c.age} ans</span>
            ),
          ),
      },
      {
        cle: "dispos",
        titre: "Disponibilités",
        triable: true,
        desDe: 760,
        largeur: 140,
        rendu: (c) => griser(c, <CelluleDispos lead={c} />),
      },
      {
        cle: "connexion",
        titre: "Connexion",
        triable: true,
        desDe: 880,
        largeur: 148,
        rendu: (c) => griser(c, <CelluleConnexion lead={c} />),
      },
      {
        cle: "discord",
        titre: "Discord",
        triable: true,
        desDe: 1130,
        largeur: 132,
        rendu: (c) => griser(c, <CelluleDiscord lead={c} discord={discordParLead?.get(c.id)} />),
      },
      {
        cle: "email",
        titre: "E-mail",
        triable: true,
        desDe: 990,
        rendu: (c) => griser(c, <span className="text-muted">{c.email ?? "—"}</span>),
      },
      // « Test », pas « Statut » : le mot ne disait pas de quoi il était le
      // statut, et le filtre correspondant porte ce nom depuis le 19-08.
      {
        cle: "statut",
        titre: "Test",
        triable: true,
        essentiel: true,
        largeur: 150,
        rendu: (c) => (
          <span className="flex items-center gap-1.5">
            <Badge ton={c.status === "submitted" ? "vert" : c.status === "started" ? "ambre" : "gris"}>
              {statusLabel(c.status)}
            </Badge>
            {estEcarte(c) && <Badge ton="rouge">Écarté</Badge>}
          </span>
        ),
      },
      {
        cle: "recu_le",
        titre: "Reçu le",
        triable: true,
        aligne: "droite",
        largeur: 124,
        rendu: (c) =>
          griser(
            c,
            <Temps.Heure
              instant={Date.parse(c.created_at)}
              fuseau={FUSEAU_DES_SHIFTS}
              format="jour"
              className="text-muted"
            />,
          ),
      },
    ],
    [discordParLead],
  );

  return (
    <div className="flex flex-col gap-3">
      <Saisie.Recherche
        valeur={filtres.recherche}
        onChange={(recherche) => setFiltres((f) => ({ ...f, recherche }))}
        placeholder="Nom, e-mail, Discord…"
        ariaLabel="Chercher un lead"
        className="sm:max-w-[420px]"
      />

      {/* Les filtres en cours, en clair et levables un par un — c'est ce
          qui remplace les rangées toujours visibles. */}
      <Saisie.FiltresActifs
        taille="lg"
        filtres={actifs.map((f) => ({
          cle: f.cle,
          etiquette: f.libelle,
          onRetirer: () => setFiltres((courant) => f.lever(courant)),
        }))}
        onToutRetirer={() => setFiltres(FILTRES_VIDES)}
        toutRetirerLabel="Tout afficher"
      />

      {/* 🚫 On masque, on ne dissimule pas. Kayla ne veut pas voir les liens
          jamais remplis, mais un écran qui les escamote sans le dire aurait
          l'air complet en ne l'étant pas — et « pourquoi j'en ai moins ? »
          coûte une session à qui la pose. Même contrat pour les écartés : le
          bouton « Écarter » de la fiche les range ici — il ne les efface pas,
          et la ligne dit combien il y en a pour qu'on puisse revenir dessus. */}
      {videsCaches > 0 && (
        <Rappel
          action="Les afficher"
          onClick={() => setFiltres((f) => ({ ...f, montrerLiensVides: true }))}
        >
          {videsCaches} lien{videsCaches > 1 ? "s" : ""} jamais rempli
          {videsCaches > 1 ? "s" : ""} — masqué{videsCaches > 1 ? "s" : ""}.
        </Rappel>
      )}
      {filtres.montrerLiensVides && (
        <Rappel
          action="Les masquer"
          onClick={() => setFiltres((f) => ({ ...f, montrerLiensVides: false }))}
        >
          Les liens jamais remplis sont affichés.
        </Rappel>
      )}
      {ecartesCaches > 0 && (
        <Rappel
          action="Les afficher"
          onClick={() => setFiltres((f) => ({ ...f, montrerEcartes: true }))}
        >
          {ecartesCaches} candidat{ecartesCaches > 1 ? "s" : ""} écarté
          {ecartesCaches > 1 ? "s" : ""} — archivé{ecartesCaches > 1 ? "s" : ""}.
        </Rappel>
      )}
      {filtres.montrerEcartes && (
        <Rappel
          action="Les masquer"
          onClick={() => setFiltres((f) => ({ ...f, montrerEcartes: false }))}
        >
          Les candidats écartés sont affichés.
        </Rappel>
      )}

      <Tableau<CandidateListItem>
        colonnes={colonnes}
        lignes={loading ? [] : affiches}
        cle={(c) => c.id}
        titre={
          filtreActif ? (
            <>
              {affiches.length} lead{affiches.length > 1 ? "s" : ""}{" "}
              <span className="font-normal text-muted">sur {total}</span>
            </>
          ) : (
            <>
              {affiches.length} lead{affiches.length > 1 ? "s" : ""}
            </>
          )
        }
        tri={{ cle: tri.colonne, sens: tri.sens }}
        onTri={(_, cle) => trierPar(cle as ColonneTri)}
        outils={
          <Button
            variant="secondary"
            size="md"
            icon={<Download className="h-3.5 w-3.5" />}
            href={CANDIDATES_CSV_HREF}
            target="_blank"
          >
            Exporter CSV
          </Button>
        }
        filtre={<LeadsFiltresPopover filtres={filtres} onChange={(suivant) => setFiltres(suivant)} />}
        filtresActifs={actifs.length}
        onLigne={(c) => onSelect(c.id)}
        vide={
          loading
            ? "Chargement…"
            : total === 0
              ? "Aucune candidature remplie pour l'instant."
              : "Aucun lead ne correspond à ces filtres."
        }
        hauteur={HAUTEUR_DU_TABLEAU}
        hauteurLigne={HAUTEUR_LIGNE}
        seuilEtroit={SEUIL_ETROIT}
      />
    </div>
  );
}

/** Une ligne qui dit ce qu'on a rangé, et le bouton pour le montrer (ou le ranger). */
function Rappel({
  children,
  action,
  onClick,
}: {
  children: ReactNode;
  action: string;
  onClick: () => void;
}) {
  return (
    <Alerte
      ton="info"
      taille="sm"
      texte={children}
      action={
        <Button variant="secondary" size="sm" onClick={onClick}>
          {action}
        </Button>
      }
    />
  );
}

/**
 * Les shifts couverts, en lettres A–D. Un candidat sans dispos affiche « — »
 * et pas « 0 créneau » : on ne lui a peut-être jamais posé la question.
 */
function CelluleDispos({ lead }: { lead: CandidateListItem }) {
  const shifts = shiftsCouverts(lead.availability);
  if (shifts.length === 0) return <span className="text-faint">—</span>;
  const total = nombreDeCreneaux(lead.availability);
  const ordonnes = SHIFTS.filter((s) => shifts.includes(s.key));
  return (
    <span className="flex items-center gap-2">
      <span className="flex gap-1">
        {ordonnes.map((s) => (
          <Infobulle key={s.key} texte={`${s.name} (${s.letter})`}>
            <span className="inline-flex">
              <Badge ton="gris">{s.letter}</Badge>
            </span>
          </Infobulle>
        ))}
      </span>
      <span className="text-[12px] text-faint">{total}/28</span>
    </span>
  );
}

/**
 * Le pseudo Discord — et, quand on sait à QUI il correspond, le bouton qui
 * ouvre la conversation privée.
 *
 * Kayla, 19-08-2026 : « un bouton où je clique pour avoir leur Discord direct,
 * pour aller les DM facilement. »
 *
 * 🪤 `stopPropagation` : la ligne entière ouvre la fiche du candidat. Sans lui,
 * cliquer le bouton ouvrirait Discord ET la fiche par-dessus l'écran.
 *
 * ⛔ Pas de bouton quand le compte est inconnu : on affiche le pseudo tel
 * qu'il a été tapé, sans le rendre cliquable. Un lien fabriqué sur un pseudo
 * approchant enverrait Kayla dans le DM de quelqu'un d'autre.
 */
function CelluleDiscord({
  lead,
  discord,
}: {
  lead: CandidateListItem;
  discord: DiscordTrouve | undefined;
}) {
  const pseudo = lead.contact_discord?.trim();
  if (!discord) return <span className="text-muted">{pseudo || "—"}</span>;

  return (
    <Infobulle
      texte={
        discord.source === "cockpit"
          ? "Discord vérifié à la connexion au cockpit — ouvrir la conversation"
          : "Pseudo retrouvé sur le serveur — ouvrir la conversation"
      }
    >
      <Button
        variant="secondary"
        size="md"
        href={lienDuDm(discord.id)}
        target="_blank"
        icon={<DiscordLogo weight="fill" className="h-3.5 w-3.5" />}
        onClick={(e) => e.stopPropagation()}
      >
        {pseudo || "Discord"}
      </Button>
    </Infobulle>
  );
}

/** Le débit : le verdict en pastille de sens, la latence médiane pour départager. */
function CelluleConnexion({ lead }: { lead: CandidateListItem }) {
  const verdict = lead.connection?.verdict ?? null;
  if (!verdict) return <span className="text-faint">—</span>;
  const rtt = lead.connection?.rtt_median_ms ?? null;
  return (
    <span className="flex items-center gap-2">
      <Badge ton={TON_CONNEXION[verdict]}>{CONN_VERDICT_LABELS[verdict]}</Badge>
      {rtt !== null && <span className="text-[12px] tabular-nums text-faint">{rtt} ms</span>}
    </span>
  );
}
